import {
  FONT_WEIGHT_KEYWORD,
  LandingPageDesign,
} from 'queries/details/usePlaybackDetailsQuery';
import { createGlobalStyle } from 'styled-components';
import { generateFontFaces } from 'utils/generateFontFaces';

export type FontFamilyVariants =
  | '100'
  | '200'
  | '300'
  | '400'
  | '500'
  | '600'
  | '700'
  | '800'
  | '900'
  | 'regular'
  | 'normal'
  | 'bold'
  | 'bolder'
  | 'lighter'
  | 'italic';

export type FontStyle =
  | '100'
  | '200'
  | '300'
  | '400'
  | '500'
  | '600'
  | '700'
  | '800'
  | '900'
  | 'regular'
  | 'italic'
  | 'normal'
  | 'bold'
  | 'bolder'
  | 'lighter';

export type FontFamilyOption = {
  value: string;
  label: string;
  variants: FontStyle[];
  files: Partial<Record<FontStyle, string>>; // each style maps to a URL
};

interface GlobalProps {
  landingPageDesign?: LandingPageDesign;
  fonts: FontFamilyOption[];
}

export const defaultFonts = [
  'Exo2',
  'Montserrat',
  'Oswald',
  'Oxygen',
  'NunitoBlack',
  'GreatVibes',
  'PlayfairDisplay',
  'BarlowCondensed',
];

const GlobalStyle = createGlobalStyle<GlobalProps>`
  ${({ fonts }) => generateFontFaces(fonts)}

  * {
    ${({ landingPageDesign }) => {
      const fallbackFont = 'Work Sans, sans-serif';
      const fontFamily = landingPageDesign?.fontFamily || fallbackFont;
      const isDefaultFont = defaultFonts.includes(fontFamily);
      return `font-family: ${isDefaultFont ? `${fontFamily}-normal` : `${fontFamily} !important`};`;
    }}
  }

  .header-logo-img, .header-logo-img * {
      max-width: ${({ landingPageDesign }) =>
        landingPageDesign?.headerLogoMaxWidth || 220}px;
       max-height: ${({ landingPageDesign }) =>
         landingPageDesign?.headerLogoMaxHeight || 85}px;
  }

  .font-roboto,
  .font-roboto * {
    font-family: 'Roboto', sans-serif !important;
  }

  .additional-text,
  .additional-text * {
    font-family: ${({ landingPageDesign }) =>
      landingPageDesign?.additionalTextFontFamily ||
      'Work Sans, sans-serif'} !important;
  }

  .header-text,
  .header-text * {
    font-family: ${({ landingPageDesign }) =>
      landingPageDesign?.headerFontFamily ||
      'Work Sans, sans-serif'} !important;
  }

  .cta-button,
  .cta-button * {
    ${({ landingPageDesign }) => {
      const {
        buttonColor,
        showButtonBorder,
        buttonBorderColor,
        buttonBorderHoverColor,
        buttonFontSize,
        buttonFontFamily,
        buttonFontCase,
        buttonFontWeight,
        buttonCornerRadius,
        buttonPaddingX,
        buttonPaddingY,
        buttonTextColor,
        buttonHoverColor,
        buttonTextHoverColor,
      } = landingPageDesign || {};

      const baseBorder = showButtonBorder
        ? `2px solid ${buttonBorderColor}`
        : 'none';
      const hoverBorder = showButtonBorder
        ? `2px solid ${buttonBorderHoverColor}`
        : 'none';

      return `
        background: ${buttonColor};
        border: ${baseBorder};
        font-size: ${buttonFontSize}px;
        font-family: ${buttonFontFamily || 'Work Sans, sans-serif'} !important;
        text-transform: ${buttonFontCase};
        font-weight: ${parseInt(buttonFontWeight || FONT_WEIGHT_KEYWORD.REGULAR)};
        border-radius: ${buttonCornerRadius}px;
        padding: ${buttonPaddingY}px ${buttonPaddingX}px;
        color: ${buttonTextColor};
        cursor: pointer;

        &:hover {
          background: ${buttonHoverColor};
          border: ${hoverBorder};
          color: ${buttonTextHoverColor};
        }
      `;
    }}
  }

  @media screen and (max-width: 525px) {
      .header-logo-img, .header-logo-img * {
        max-width: 100%;
      }
  }
`;

export default GlobalStyle;
